import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditIcon from '@mui/icons-material/Edit'
import Groups from '@mui/icons-material/Groups'
import ShareIcon from '@mui/icons-material/Share'
import { alpha, Button, ButtonBase, IconButton, Stack, Typography } from '@mui/material'

import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DisabledReasonTooltip } from './disabled-reason-tooltip'

interface ReorderBasketHeaderProps {
  variant: 'active' | 'inactive'
  friendlyId: string
  basketHeaderName: string
  copyFriendlyId: () => void
  disableDelete?: boolean
  /** Disables interactions (delete, close, rename) while a bulk operation is in flight. */
  disableActions?: boolean
  /** Hides the copy-link pill — there's nothing worth sharing when the basket is empty. */
  hideCopy?: boolean
  onDelete?: () => void
  onClose?: () => void
  onNameChange?: (name: string) => void
  /** Team-order baskets render in the tenant's secondary palette, with a Groups icon. */
  colorVariant?: 'primary' | 'secondary'
  lockedTooltip?: string
}

export const ReorderBasketHeader: React.FC<ReorderBasketHeaderProps> = ({
  variant,
  friendlyId,
  basketHeaderName,
  copyFriendlyId,
  disableDelete,
  disableActions,
  hideCopy,
  onDelete,
  onClose,
  onNameChange,
  colorVariant = 'primary',
  lockedTooltip,
}) => {
  const isCateringTeamOrderVariant = colorVariant === 'secondary'
  const isLocked = Boolean(lockedTooltip)
  const nameRef = useRef<HTMLSpanElement>(null)
  const showRenameAffordance = Boolean(onNameChange) && !disableActions
  const canRename = showRenameAffordance && !isLocked
  const { t } = useTranslation()
  const [justCopied, setJustCopied] = useState(false)
  const copiedResetRef = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => () => clearTimeout(copiedResetRef.current), [])

  const handleShareLinkClick = () => {
    copyFriendlyId()
    setJustCopied(true)
    clearTimeout(copiedResetRef.current)
    copiedResetRef.current = setTimeout(() => setJustCopied(false), 2000)
  }

  const focusNameForEdit = () => {
    const node = nameRef.current
    if (!node) return
    node.focus()
    const selection = window.getSelection()
    if (!selection) return
    const range = document.createRange()
    range.selectNodeContents(node)
    selection.removeAllRanges()
    selection.addRange(range)
  }

  if (variant === 'inactive') {
    return (
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          bgcolor: isCateringTeamOrderVariant
            ? (theme) => alpha(theme.palette.secondary.main, 0.08)
            : 'background.paper',
          border: '1px solid',
          borderColor: isCateringTeamOrderVariant ? 'secondary.main' : 'divider',
          borderRadius: '12px',
          padding: '7px 14px',
          cursor: 'pointer',
          transition: 'border-color .15s, box-shadow .15s',
          boxShadow: 1,
          '&:hover': {
            borderColor: isCateringTeamOrderVariant ? 'secondary.main' : 'primary.main',
            boxShadow: 2,
          },
        }}>
        <Typography
          sx={{ fontSize: 13, fontWeight: 500, color: 'text.secondary' }}
          data-cy="basketHeaderName">
          {basketHeaderName}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <ButtonBase
            data-testid="ContentCopyIcon"
            component="button"
            type="button"
            disabled={disableActions}
            onClick={(e) => {
              e.stopPropagation()
              copyFriendlyId()
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              padding: '2px 4px',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
            aria-label={`Copy basket id ${friendlyId}`}>
            <ContentCopyIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: 'text.disabled',
                letterSpacing: '.5px',
              }}>
              {friendlyId}
            </Typography>
          </ButtonBase>
          {onDelete && (
            <IconButton
              data-testid="deleteDraftOrderIcon"
              size="small"
              disabled={disableDelete}
              onClick={(e) => {
                e.stopPropagation()
                onDelete()
              }}
              sx={{
                width: 26,
                height: 26,
                borderRadius: '6px',
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.error.main, 0.12),
                  '& svg': { color: 'error.main' },
                },
              }}
              aria-label="Delete draft">
              <DeleteOutlineIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            </IconButton>
          )}
        </Stack>
      </Stack>
    )
  }

  // Active variant
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{ marginBottom: 1.75 }}>
      <Stack direction="row" alignItems="center" spacing={0.875} flex={1} minWidth={0}>
        {isCateringTeamOrderVariant && (
          <Groups fontSize="small" sx={{ color: 'common.white', flexShrink: 0 }} />
        )}
        <Typography
          // Standard body2 weight/size, matching the original (V1) basket
          // header — the earlier V2 build added fontWeight:700 + fontSize:15
          // which read "too bold". Only the V2 inline-edit wiring (ref,
          // contentEditable, focus underline) is layered on top.
          variant="body2"
          ref={nameRef}
          component="span"
          contentEditable={canRename}
          suppressContentEditableWarning
          spellCheck={false}
          onKeyDown={(e) => {
            // Prevent Enter from inserting a newline (or <br>) into the
            // contentEditable element; commit via blur instead. Ignore Enter
            // while an IME composition is active so non-Latin input methods
            // can confirm candidates without saving a half-typed value.
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault()
              ;(e.currentTarget as HTMLElement).blur()
            }
          }}
          onBlur={(e) => {
            const next = (e.currentTarget.textContent ?? '').trim()
            // Don't overwrite the saved name with an empty value, and skip
            // no-op writes when the user blurred without changing anything.
            if (!next || next === basketHeaderName) {
              e.currentTarget.textContent = basketHeaderName
              return
            }
            onNameChange?.(next)
          }}
          sx={{
            color: 'common.white',
            outline: 'none',
            borderBottom: '1px solid transparent',
            transition: 'border-color .15s',
            minWidth: 20,
            // Wrap rather than clip — nowrap+overflow:hidden previously cut off
            // long names ("a really long basket n…"). overflowWrap breaks a
            // single long unbroken token so it can't run past the buttons.
            overflowWrap: 'anywhere',
            cursor: canRename ? 'text' : 'default',
            '&:focus': { borderBottomColor: 'rgba(255,255,255,.35)' },
          }}>
          {basketHeaderName}
        </Typography>
        {showRenameAffordance && (
          <DisabledReasonTooltip reason={lockedTooltip}>
            <ButtonBase
              component="button"
              type="button"
              disabled={isLocked}
              onClick={focusNameForEdit}
              data-cy="Edit-draft-order-name"
              aria-label="Rename basket"
              sx={{
                padding: '2px',
                borderRadius: '4px',
                flexShrink: 0,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'rgba(255,255,255,.12)' },
                '&:focus-visible': { outline: '1px solid rgba(255,255,255,.5)' },
                '&.Mui-disabled': { opacity: 0.45 },
              }}>
              <EditIcon sx={{ fontSize: 13, color: 'rgba(255,255,255,.7)' }} />
            </ButtonBase>
          </DisabledReasonTooltip>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={0.875}>
        {!hideCopy && isCateringTeamOrderVariant && (
          <Button
            size="small"
            variant="contained"
            disabled={disableActions}
            onClick={handleShareLinkClick}
            startIcon={<ShareIcon />}>
            {justCopied ? t('marketplace:copied') : t('marketplace:shareLink')}
          </Button>
        )}
        {!hideCopy && !isCateringTeamOrderVariant && (
          <ButtonBase
            component="button"
            type="button"
            disabled={disableActions}
            onClick={copyFriendlyId}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
              bgcolor: 'rgba(255,255,255,.15)',
              borderRadius: '100px',
              padding: '4px 11px',
              border: '1px solid rgba(255,255,255,.15)',
              cursor: 'pointer',
            }}
            aria-label={`Copy basket id ${friendlyId}`}>
            <ContentCopyIcon sx={{ fontSize: 12, color: 'rgba(255,255,255,.65)' }} />
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 700,
                color: 'rgba(255,255,255,.9)',
                letterSpacing: '.8px',
              }}>
              {friendlyId}
            </Typography>
          </ButtonBase>
        )}

        {onDelete && (
          <DisabledReasonTooltip reason={lockedTooltip}>
            <IconButton
              size="small"
              disabled={disableDelete || disableActions || isLocked}
              onClick={onDelete}
              sx={{
                width: 28,
                height: 28,
                bgcolor: 'rgba(255,255,255,.12)',
                border: '1px solid rgba(255,255,255,.15)',
                borderRadius: '8px',
                '&:hover': { bgcolor: 'rgba(255,255,255,.22)' },
                '&.Mui-disabled': { opacity: 0.45 },
              }}
              aria-label="Delete basket">
              <DeleteOutlineIcon sx={{ fontSize: 14, color: 'rgba(255,255,255,.8)' }} />
            </IconButton>
          </DisabledReasonTooltip>
        )}

        {onClose && (
          <IconButton
            size="small"
            disabled={disableActions}
            onClick={onClose}
            sx={{ color: 'common.white' }}
            aria-label="Close basket">
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Stack>
    </Stack>
  )
}
